import React, { useState, useEffect, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "sonner";

import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	CircularProgress,
	Select,
	InputLabel,
	MenuItem,
	FormControl,
	TextField,
	InputAdornment,
	IconButton,
	FormControlLabel,
	Switch
} from '@material-ui/core';

import { Visibility, VisibilityOff } from '@material-ui/icons';

import { makeStyles, withStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const GreenSwitch = withStyles((theme) => ({
	switchBase: {
		color: theme.palette.type === "dark" ? "#64748B" : "#CBD5E1",
		"&$checked": {
			color: "#0B3C84",
		},
		"&$checked + $track": {
			backgroundColor: "#0B3C84",
		},
	},
	checked: {},
	track: {
		backgroundColor: theme.palette.type === "dark" ? "#475569" : "#E2E8F0",
	},
}))(Switch);

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	dialogPaper: {
		borderRadius: 14,
	},
	dialogTitle: {
		fontFamily: '"Inter", "Poppins", sans-serif',
		fontWeight: 700,
		fontSize: 18,
		color: theme.palette.type === "dark" ? "#F1F5F9" : "#1E293B",
		padding: theme.spacing(2, 3),
	},
	dialogContent: {
		padding: theme.spacing(2, 3),
	},
	dialogActions: {
		padding: theme.spacing(2, 3),
		gap: theme.spacing(1),
	},
	textField: {
		width: "100%",
		"& .MuiOutlinedInput-root": {
			borderRadius: 10,
			fontFamily: '"Inter", "Poppins", sans-serif',
			"& fieldset": {
				borderColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.12)" : "#E2E8F0",
			},
			"&:hover fieldset": {
				borderColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.2)" : "#CBD5E1",
			},
			"&.Mui-focused fieldset": {
				borderColor: "#0B3C84",
			},
		},
		"& .MuiInputLabel-outlined": {
			fontFamily: '"Inter", "Poppins", sans-serif',
			"&.Mui-focused": {
				color: "#0B3C84",
			},
		},
	},
	multFieldLine: {
		marginBottom: theme.spacing(2),
	},
	switchLabel: {
		fontFamily: '"Inter", "Poppins", sans-serif',
		fontSize: 14,
	},
	btnWrapper: {
		position: "relative",
	},
	buttonProgress: {
		color: "#0B3C84",
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
	buttonCancel: {
		fontFamily: '"Inter", "Poppins", sans-serif',
		fontWeight: 600,
		fontSize: 14,
		textTransform: "none",
		borderRadius: 10,
		padding: "8px 24px",
		borderColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.12)" : "#E2E8F0",
		color: theme.palette.type === "dark" ? "#F1F5F9" : "#1E293B",
		"&:hover": {
			borderColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.2)" : "#CBD5E1",
			backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "#F8FAFC",
		},
	},
	buttonSave: {
		fontFamily: '"Inter", "Poppins", sans-serif',
		fontWeight: 600,
		fontSize: 14,
		textTransform: "none",
		borderRadius: 10,
		padding: "8px 24px",
		backgroundColor: "#0B3C84",
		color: "#FFFFFF",
		boxShadow: "none",
		"&:hover": {
			backgroundColor: "#082D63",
			boxShadow: "0 2px 8px rgba(11,60,132,0.3)",
		},
	},
}));

const CompanySchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(50, "Too Long!")
		.required("Nome é obrigatório"),
	email: Yup.string().email("Email é inválido").required("E-mail é obrigatório"),
	numberAttendants: Yup.number(),
	numberConections: Yup.number(),
});

const CompanyModal = ({ open, onClose, companyId }) => {
	const classes = useStyles();

	const initialState = {
		name: "",
		email: "",
		passwordDefault: "",
		numberAttendants: 1,
		numberConections: 1,
		status: false
	};

	const [company, setCompany] = useState(initialState);
	const [showPassword, setShowPassword] = useState(false);

	useEffect(() => {
		const fetchCompany = async () => {
			if (!companyId) return;
			try {
				const { data } = await api.get(`/companies/listPlan/${companyId}`);
				setCompany(prevState => {
					return { ...prevState, ...data };
				});
			} catch (err) {
				toastError(err);
			}
		};

		fetchCompany();
	}, [companyId, open]);

	const handleClose = () => {
		onClose();
		setCompany(initialState);
	};

	const handleSaveCompany = async values => {
		const companyData = { ...values };
		try {
			if (companyId) {
				await api.put(`/companies/${companyId}`, companyData);
			} else {
				await api.post("/companies", companyData);
			}
			toast.success(i18n.t("companyModal.success"));
		} catch (err) {
			toastError(err);
		}
		handleClose();
	};

	return (
		<div className={classes.root}>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="xs"
				fullWidth
				scroll="paper"
				PaperProps={{
					className: classes.dialogPaper,
				}}
			>
				<DialogTitle className={classes.dialogTitle}>
					{companyId
						? `${i18n.t("companyModal.title.edit")}`
						: `${i18n.t("companyModal.title.add")}`}
				</DialogTitle>
				<Formik
					initialValues={company}
					enableReinitialize={true}
					validationSchema={CompanySchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveCompany(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ values, touched, errors, isSubmitting, setFieldValue }) => (
						<Form>
							<DialogContent className={classes.dialogContent} dividers>
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("companyModal.form.name")}
										autoFocus
										name="name"
										error={touched.name && Boolean(errors.name)}
										helperText={touched.name && errors.name}
										variant="outlined"
										margin="dense"
										fullWidth
										className={classes.textField}
									/>
								</div>
								<div className={classes.multFieldLine}>
									<FormControlLabel
										control={
											<GreenSwitch
												checked={values.status}
												onChange={(e) => {
													setFieldValue("status", e.target.checked);
												}}
											/>
										}
										label={<span className={classes.switchLabel}>Ativo</span>}
									/>
								</div>
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("companyModal.form.email")}
										name="email"
										error={touched.email && Boolean(errors.email)}
										helperText={touched.email && errors.email}
										variant="outlined"
										margin="dense"
										fullWidth
										className={classes.textField}
									/>
								</div>
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										name="passwordDefault"
										variant="outlined"
										margin="dense"
										label={i18n.t("companyModal.form.passwordDefault")}
										error={touched.passwordDefault && Boolean(errors.passwordDefault)}
										helperText={touched.passwordDefault && errors.passwordDefault}
										type={showPassword ? 'text' : 'password'}
										className={classes.textField}
										InputProps={{
											endAdornment: (
												<InputAdornment position="end">
													<IconButton
														aria-label="toggle password visibility"
														onClick={() => setShowPassword((e) => !e)}
													>
														{showPassword ? <VisibilityOff /> : <Visibility />}
													</IconButton>
												</InputAdornment>
											)
										}}
										fullWidth
									/>
								</div>
								
								{/* <div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("companyModal.form.numberAttendants")}
										name="numberAttendants"
										error={touched.numberAttendants && Boolean(errors.numberAttendants)}
										helperText={touched.numberAttendants && errors.numberAttendants}
										variant="outlined"
										margin="dense"
										type="number"
										fullWidth
										style={
											// console.log('touched', touched)
											console.log('value', values)
										}
									/>
								</div> */}
								{/* <div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("companyModal.form.numberConections")}
										name="numberConections"
										error={touched.numberConections && Boolean(errors.numberConections)}
										helperText={touched.numberConections && errors.numberConections}
										variant="outlined"
										margin="dense"
										type="number"
										fullWidth
									/>
								</div> */}
							</DialogContent>
							<DialogActions className={classes.dialogActions}>
								<Button
									onClick={handleClose}
									disabled={isSubmitting}
									variant="outlined"
									className={classes.buttonCancel}
								>
									{i18n.t("companyModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									disabled={isSubmitting}
									variant="contained"
									className={`${classes.buttonSave} ${classes.btnWrapper}`}
								>
									{companyId
										? `${i18n.t("companyModal.buttons.okEdit")}`
										: `${i18n.t("companyModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default CompanyModal;
